import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
  IconButton,
  Avatar,
  Autocomplete,
  TextField,
  Grid,
} from '@mui/material';
import {
  Info,
  Plus,
  PencilSimple,
  Notepad,
  Heart,
  Books,
  ArrowRight,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { useParams, useNavigate, Link } from 'react-router';
import { useState, useEffect } from 'react';
import { Api } from '../api';
import { serverImageUrl, iriToId } from '../util';
import EditNoteModal from './modals/EditNoteModal';
import CreateNoteModal from './modals/CreateNoteModal';
import SoloLoading from './SoloLoading';
import EditContentTagsModal from './modals/EditContentTagsModal';
import EditMetricsModal from './modals/EditMetricsModal';
import UserProgramSection from './UserProgramSection';
import useApiCallable from '../api/use-api-callable';
import { toast } from 'sonner';
import { format, parseISO, isValid, differenceInCalendarDays } from 'date-fns';
import { useInvalidation } from 'api-read-hook';
import HealthProfileSection from './HealthProfileSection';
import { Model } from '../models';
import { useAuthRoles } from '../hooks';
import ScreenError from './ScreenError';
import SubscriptionBadge from './SubscriptionBadge';
import { useUserStatusContext } from '../contexts';

interface UserDashboardProps {
  viewMode?: 'admin' | 'clinician';
  stats: Api.UserStats.V;
}

export default function UserDashboard({
  viewMode = 'admin',
  stats,
}: UserDashboardProps) {
  const { hasEditAccess } = useUserStatusContext();
  const authRoles = useAuthRoles();
  const { id } = useParams();
  const navigate = useNavigate();
  const callApi = useApiCallable();
  const { invalidateExact } = useInvalidation();

  const classificationsApi = Api.ClientClassification.useReadCollection();

  const CLASSIFICATION_OPTIONS = (classificationsApi.data?.member || []).map(
    (c) => ({
      label: c.name,
      description: c.description ?? '',
      value: c['@id'],
    }),
  );

  const [selectedClassifications, setSelectedClassifications] = useState<
    Array<{ label: string; description: string; value: string }>
  >([]);

  useEffect(() => {
    if (stats?.user.classifications && classificationsApi.data?.member) {
      const mappedClassifications = (stats.user.classifications || []).map(
        (c) => {
          const apiClassification = (
            classificationsApi.data?.member || []
          ).find((x) => x['@id'] === c['@id']);
          return {
            label: c.name,
            description: apiClassification?.description || '',
            value: c['@id'],
          };
        },
      );
      setSelectedClassifications(mappedClassifications);
    }
  }, [stats?.user.classifications, classificationsApi.data?.member]);

  const [createNoteOpen, setCreateNoteOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<
    Api.UserStats.V['notes'][0] | null
  >(null);
  const myClinicianStats = stats?.clinicians.find(
    (c) => c.type === (authRoles.physician ? 'physician' : 'coach'),
  );

  const [editContentTagsModalOpen, setEditContentTagsModalOpen] =
    useState(false);

  const [editMetricsModalOpen, setEditMetricsModalOpen] = useState(false);
  const [isEditingWeight, setIsEditingWeight] = useState(false);
  const [weightValue, setWeightValue] = useState<number>(
    stats?.user.weight ?? 0,
  );
  const tracker = stats?.metricsOverview?.trackerSetting;
  const trackerChips = [
    { key: 'food', label: 'Food', enabled: !!tracker?.food },
    { key: 'water', label: 'Water', enabled: !!tracker?.water },
    { key: 'mood', label: 'Mood', enabled: !!tracker?.mood },
    { key: 'symptoms', label: 'Symptoms', enabled: !!tracker?.symptoms },
    { key: 'cycle', label: 'Cycle', enabled: !!tracker?.cycleManual },
    { key: 'steps', label: 'Steps', enabled: !!tracker?.steps },
    { key: 'exercise', label: 'Exercise', enabled: !!tracker?.exercise },
    { key: 'sleep', label: 'Sleep', enabled: !!tracker?.sleep },
    {
      key: 'dailyThoughts',
      label: 'Daily Thoughts',
      enabled: !!tracker?.dailyThoughts,
    },
  ];

  if (classificationsApi.error) return <ScreenError {...classificationsApi} />;
  if (!classificationsApi.data) return <SoloLoading />;

  const handleViewMetrics = () => {
    const basePath = '/users';
    navigate(`${basePath}/${id}/metrics/symptoms-cycle`);
  };

  const handleViewContentInfo = () => {
    const basePath = '/users';
    navigate(`${basePath}/${id}/content-info`);
  };

  const handleEditMetrics = () => {
    setEditMetricsModalOpen(true);
  };

  const computeAge = (dob: string | null | undefined) => {
    if (!dob) return '-';
    const d = new Date(dob);
    if (isNaN(d.getTime())) return '-';
    const diff = Date.now() - d.getTime();
    const ageDt = new Date(diff);
    return Math.abs(ageDt.getUTCFullYear() - 1970).toString();
  };

  const computeBmi = (heightCm?: number | null, weightKg?: number | null) => {
    if (!heightCm || !weightKg) return null;
    const h = heightCm / 100;
    const bmi = weightKg / (h * h);
    return bmi.toFixed(1);
  };

  const formatDaysAgo = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const date = parseISO(dateStr);
    if (!isValid(date)) return '-';
    const diffDays = differenceInCalendarDays(new Date(), date);
    if (diffDays === 0) return 'Today';
    return `${diffDays} Day${diffDays === 1 ? '' : 's'} ago`;
  };

  const describeBleeding = (isPeriodBleeding?: boolean | null) => {
    if (isPeriodBleeding == null) return '-';
    return isPeriodBleeding ? 'During period' : 'Bleeding outside of period';
  };

  const capitalize = (s?: string | null) => {
    if (!s) return '-';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getDisplayContentTags = () => {
    const tags = stats?.user.contentTags ?? [];
    if (!tags.length) return [] as string[];
    return tags
      .map((t) => `${t.category.name} / ${t.internalName || t.userFacingName}`)
      .filter(Boolean) as string[];
  };

  const RecentlyViewedContent = () => {
    const articles = (stats?.recentlyViewedLibraryContent || []).slice(0, 4);

    return (
      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-level1)',
          borderRadius: 2,
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">Recently Viewed Content</Typography>
          <Grid container spacing={2}>
            {articles.map((item, index) => (
              <Grid item xs={3} key={index}>
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level2)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Stack direction="row" alignItems="stretch">
                    {item.thumb ? (
                      <Box
                        component="img"
                        src={serverImageUrl({
                          id: item.thumb.id,
                          w: 60,
                        })}
                        alt={item.title}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'grey.200',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Books size={24} />
                      </Box>
                    )}
                    <Box sx={{ p: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>
    );
  };

  if (!id) return null;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          User Classification
        </Typography>
        <Autocomplete
          multiple
          options={CLASSIFICATION_OPTIONS}
          getOptionLabel={(option) => option.label}
          value={selectedClassifications}
          onChange={async (_e, value) => {
            if (!hasEditAccess) return;
            setSelectedClassifications(value);
            if (!id) return;
            const resp = await callApi(`/users/${id}/as-admin`, {
              method: 'PATCH',
              jsonBody: {
                classifications: value.map((v) => v.value),
              },
            });
            if (resp) {
              toast.success('Classifications updated');
            }
          }}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Stack>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              </Stack>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.label}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} variant="outlined" size="small" label="" />
          )}
        />
      </Box>

      <Stack direction="row" spacing={3}>
        <Card sx={{ flex: 1 }}>
          <CardHeader
            avatar={
              <Avatar>
                <Info size={24} />
              </Avatar>
            }
            title="About"
          />
          <CardContent>
            <Stack spacing={2}>
              {viewMode === 'clinician' && stats && (
                <>
                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Name
                    </Typography>
                    <Typography>{stats.user.name}</Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Email
                    </Typography>
                    <Typography>{stats.user.email}</Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Age
                    </Typography>
                    <Typography>{computeAge(stats.user.dob)}</Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Country of Residence
                    </Typography>
                    <Typography>{stats.user.country ?? '-'}</Typography>
                  </Stack>
                </>
              )}

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Height
                </Typography>
                <Typography>
                  {stats?.user.height != null ? `${stats.user.height} cm` : '-'}
                </Typography>
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  Weight
                </Typography>
                {viewMode === 'admin' ? (
                  isEditingWeight ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={weightValue}
                        onChange={(e) => setWeightValue(Number(e.target.value))}
                        placeholder="Enter weight"
                      />
                      <Button
                        onClick={async () => {
                          if (!id) return;
                          const resp = await callApi(`/users/${id}/as-admin`, {
                            method: 'PATCH',
                            jsonBody: {
                              email: stats?.user.email || '',
                              weight: Number(weightValue) || null,
                            },
                          });
                          if (resp) {
                            invalidateExact(`/users/${id}/stats`);
                            toast.success('Weight updated');
                            setIsEditingWeight(false);
                          }
                        }}
                      >
                        Save
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>{weightValue} kg</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setIsEditingWeight(true)}
                      >
                        <PencilSimple fontSize="small" />
                      </IconButton>
                    </Stack>
                  )
                ) : (
                  <Typography>{weightValue} kg</Typography>
                )}
              </Stack>

              <Stack direction="row">
                <Typography variant="subtitle1" sx={{ width: '50%' }}>
                  BMI
                </Typography>
                <Typography>
                  {stats?.user.height && stats?.user.weight
                    ? computeBmi(stats.user.height, stats.user.weight)
                    : '-'}
                </Typography>
              </Stack>

              {viewMode === 'clinician' && stats && (
                <>
                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Registration Date
                    </Typography>
                    <Typography>
                      {format(stats.user.registeredAt, 'd MMM yyyy')}
                    </Typography>
                  </Stack>

                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Subscription
                    </Typography>
                    <Stack
                      direction={
                        stats.subscription.licence?.revokedAt ? 'column' : 'row'
                      }
                      alignItems={
                        stats.subscription.licence?.revokedAt
                          ? 'flex-start'
                          : 'center'
                      }
                      spacing={1}
                    >
                      <SubscriptionBadge subscription={stats.subscription} />
                      {stats?.subscription.licence?.team && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Typography variant="body2">B2B:</Typography>
                          <Typography variant="body2">
                            {Model.User.getB2BLabel(stats.subscription)}
                          </Typography>
                          <IconButton
                            component={Link}
                            to={`/users?organisationTeam=${iriToId(stats.subscription.licence?.team['@id'])}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            color="primary"
                          >
                            <ArrowSquareOut size={16} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction="row">
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Appointments
                    </Typography>
                    <Stack>
                      <Typography>
                        {myClinicianStats?.appointmentsHeld ?? 0} held
                      </Typography>
                      <Typography>
                        {myClinicianStats?.appointmentsScheduled ?? 0} scheduled
                      </Typography>
                      <Typography>
                        {myClinicianStats?.sessionsInSubscription ?? 0} in
                        subscription
                      </Typography>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardHeader
            avatar={
              <Avatar>
                <Notepad size={24} />
              </Avatar>
            }
            title="Notes"
            action={
              <Button
                variant="outlined"
                startIcon={<Plus fontSize="small" />}
                onClick={() => setCreateNoteOpen(true)}
              >
                New Note
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              {stats.notes.length > 0 ? (
                stats.notes.map((note) => (
                  <Box
                    key={note['@id']}
                    sx={{
                      bgcolor: 'var(--mui-palette-background-level1)',
                      borderRadius: 2,
                      p: 2,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedNote(note)}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {format(note.date, 'd MMM yyyy')} | by{' '}
                            {note.addedByMe ? 'you' : note.addedByName}
                          </Typography>
                          <Typography variant="subtitle1">
                            {note.title}
                          </Typography>
                        </Stack>
                        <IconButton>
                          <ArrowRight size={20} />
                        </IconButton>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {note.text}
                      </Typography>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No notes added yet – click the button on the right to create
                  one!
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <Heart size={24} />
            </Avatar>
          }
          title="Metrics Overview"
          subheader="for the last 7 days"
        />
        <CardContent>
          <Stack direction="row" spacing={3}>
            <Stack
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">
                Symptoms with highest severity
              </Typography>
              <Grid container spacing={2}>
                {(stats?.metricsOverview?.topSymptoms ?? []).map(
                  (item, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography variant="h5">{item.symptom.name}</Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {item.avgSeverity != null
                          ? item.avgSeverity.toFixed(1)
                          : '-'}
                      </Typography>
                    </Grid>
                  ),
                )}
              </Grid>
            </Stack>

            <Stack
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">Cycle</Typography>
              <Stack>
                <Typography variant="h5" mb={1}>
                  {formatDaysAgo(stats?.metricsOverview?.lastMetricCycle?.date)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {describeBleeding(
                    stats?.metricsOverview?.lastMetricCycle?.isPeriodBleeding,
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {capitalize(
                    stats?.metricsOverview?.lastMetricCycle?.flowType,
                  )}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">
                Average macronutrients/day
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="body2" color="text.secondary">
                      Carbs
                    </Typography>
                    <Typography variant="h5">
                      {stats?.metricsOverview?.carbs != null
                        ? `${stats.metricsOverview.carbs}g`
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="body2" color="text.secondary">
                      Protein
                    </Typography>
                    <Typography variant="h5">
                      {stats?.metricsOverview?.protein != null
                        ? `${stats.metricsOverview.protein}g`
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="body2" color="text.secondary">
                      Fat
                    </Typography>
                    <Typography variant="h5">
                      {stats?.metricsOverview?.fat != null
                        ? `${stats.metricsOverview.fat}g`
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>

            <Stack
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                flex: 1,
                gap: 2,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">
                  Enabled Metric Trackers
                </Typography>
                {hasEditAccess && (
                  <Button
                    variant="text"
                    startIcon={<PencilSimple />}
                    onClick={handleEditMetrics}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {trackerChips
                    .filter((metric) => metric.enabled)
                    .map((metric) => (
                      <Chip
                        key={metric.key}
                        label={metric.label}
                        color="default"
                      />
                    ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
        <CardHeader
          sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
          title={
            <Button
              variant="text"
              endIcon={<ArrowRight />}
              onClick={handleViewMetrics}
            >
              All Metrics Data
            </Button>
          }
        />
      </Card>

      <UserProgramSection userStats={stats} />

      <HealthProfileSection stats={stats} />

      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <Books size={24} />
            </Avatar>
          }
          title="Library Content"
        />
        <CardContent>
          <Stack spacing={3}>
            <RecentlyViewedContent />

            <Box
              sx={{
                bgcolor: 'var(--mui-palette-background-level1)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="subtitle1">Article Questions</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={`${stats?.articleResponsesByUser ?? 0} responses`}
                    color="primary"
                  />
                  <Typography variant="body2" color="text.secondary">
                    in the last 7 days
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                bgcolor: 'var(--mui-palette-background-level1)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1">Content Tags</Typography>
                  {hasEditAccess && (
                    <Button
                      variant="text"
                      startIcon={<PencilSimple />}
                      onClick={() => setEditContentTagsModalOpen(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  We give content recommendations to the user based on these
                  tags
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {getDisplayContentTags().map((tag, index) => (
                      <Chip
                        key={`${tag}-${index}`}
                        label={tag}
                        color="default"
                      />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
        <CardHeader
          sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
          title={
            <Button
              variant="text"
              endIcon={<ArrowRight />}
              onClick={handleViewContentInfo}
            >
              All Content Info
            </Button>
          }
        />
      </Card>

      {createNoteOpen && (
        <CreateNoteModal
          userId={stats.user.id}
          onClose={() => setCreateNoteOpen(false)}
        />
      )}

      {selectedNote && (
        <EditNoteModal
          userId={stats.user.id}
          onClose={() => setSelectedNote(null)}
          existing={selectedNote}
        />
      )}

      <EditContentTagsModal
        open={editContentTagsModalOpen}
        onClose={() => setEditContentTagsModalOpen(false)}
        userId={id}
      />

      <EditMetricsModal
        open={editMetricsModalOpen}
        onClose={() => setEditMetricsModalOpen(false)}
        userId={id}
      />
    </Stack>
  );
}
